import React, { useState, useContext, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AppContext } from '../App';
import { CURRENCIES, getAvatarUrl } from '../data/mockData';
import { useAuth, DASHBOARD_ROUTES } from '../context/AuthContext';
import { useFeatureFlags } from '../context/FeatureFlagContext';
import { useI18n, SUPPORTED_LANGUAGES } from '../context/I18nContext';
import './Navbar.css';

export default function Navbar({ onSignIn }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { setModalOpen, currency, setCurrency } = useContext(AppContext);
  const { user, logout } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const { lang, changeLang } = useI18n();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const currRef = useRef(null);
  const userRef = useRef(null);
  const langRef = useRef(null);

  const current = CURRENCIES.find(c => c.code === currency) || CURRENCIES[0];

  useEffect(() => {
    const handler = (e) => {
      if (currRef.current && !currRef.current.contains(e.target)) setCurrencyOpen(false);
      if (userRef.current && !userRef.current.contains(e.target)) setUserMenuOpen(false);
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/mentors', label: 'Find Mentors' },
    ...(isEnabled('certifications') ? [{ path: '/certifications', label: 'Certifications' }] : []),
    ...(isEnabled('mentorEvents') ? [{ path: '/events', label: 'Events' }] : []),
    // Only show "Become a Mentor" to guests and mentees — not to existing mentors or admins
    ...(!user || user.role === 'mentee' ? [{ path: '/become-mentor', label: 'Become a Mentor' }] : []),
    { path: '/training', label: 'Training' },
    { path: '/news',     label: 'News' },
    { path: '/blog',     label: 'Help' },
  ];

  const handleLogout = () => { logout(); navigate('/'); setUserMenuOpen(false); };

  return (
    <nav className="nav">
      <Link to="/" className="nav-logo">
        <div className="nav-logo-mark">D</div>
        <span className="nav-logo-text">Dev<span>Brains</span></span>
      </Link>

      <div className="nav-tabs">
        {navLinks.map(link => (
          <Link key={link.path} to={link.path} className={`nav-tab ${location.pathname === link.path ? 'active' : ''}`}>{link.label}</Link>
        ))}
      </div>

      <div className="nav-right">
        {/* Language Selector — only when feature enabled and >1 lang available */}
        {isEnabled('languageSelector') && SUPPORTED_LANGUAGES.length > 1 && (
          <div className="language-selector" ref={langRef}>
            <button className="currency-btn" onClick={() => setLangOpen(!langOpen)}>
              <span>{SUPPORTED_LANGUAGES.find(l => l.code === lang)?.flag || '🌐'}</span>
              <span>{lang.toUpperCase()}</span>
              <span className="curr-arrow">{langOpen ? '▲' : '▼'}</span>
            </button>
            {langOpen && (
              <div className="currency-dropdown" style={{ minWidth: 180 }}>
                <div className="curr-dropdown-title">Language</div>
                {SUPPORTED_LANGUAGES.map(l => (
                  <button key={l.code} className={`curr-option ${lang === l.code ? 'selected' : ''}`}
                    onClick={() => { changeLang(l.code); setLangOpen(false); }}>
                    <span className="curr-flag">{l.flag}</span>
                    <div className="curr-info"><span className="curr-code">{l.label}</span></div>
                    {lang === l.code && <span className="curr-check">✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Currency */}
        <div className="currency-selector" ref={currRef}>
          <button className="currency-btn" onClick={() => setCurrencyOpen(!currencyOpen)}>
            <span>{current.flag}</span><span>{current.code}</span><span className="curr-arrow">{currencyOpen ? '▲' : '▼'}</span>
          </button>
          {currencyOpen && (
            <div className="currency-dropdown">
              <div className="curr-dropdown-title">Select Currency</div>
              {CURRENCIES.map(c => (
                <button key={c.code} className={`curr-option ${currency === c.code ? 'selected' : ''}`} onClick={() => { setCurrency(c.code); setCurrencyOpen(false); }}>
                  <span className="curr-flag">{c.flag}</span>
                  <div className="curr-info"><span className="curr-code">{c.code}</span><span className="curr-name">{c.name}</span></div>
                  <span className="curr-symbol">{c.symbol}</span>
                  {currency === c.code && <span className="curr-check">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Auth / User area */}
        {user ? (
          <div className="user-menu-wrap" ref={userRef}>
            <button className="user-menu-btn" onClick={() => setUserMenuOpen(!userMenuOpen)}>
              <div className="nav-user-av-wrap">
                <img src={getAvatarUrl(user.avatar, user.avatarBg, 32)} alt={user.name} className="nav-user-av"
                  onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                <div className="nav-user-av-fb" style={{ background: `#${user.avatarBg}` }}>{user.initials}</div>
              </div>
              <div className="nav-user-info">
                <span className="nav-user-name">{user.name.split(' ')[0]}</span>
                <span className="nav-user-role">{user.role}</span>
              </div>
              <span className="curr-arrow">{userMenuOpen ? '▲' : '▼'}</span>
            </button>
            {userMenuOpen && (
              <div className="user-dropdown">
                <div className="user-dropdown-header">
                  <div className="nav-user-name" style={{ fontSize: '.84rem' }}>{user.name}</div>
                  <div className="nav-user-role" style={{ marginTop: 2, textTransform: 'capitalize' }}>{user.role} Account</div>
                </div>
                <Link to={DASHBOARD_ROUTES[user.role]} className="user-dd-item" onClick={() => setUserMenuOpen(false)}>📊 My Dashboard</Link>
                <Link to={`/dashboard/${user.role}/profile`} className="user-dd-item" onClick={() => setUserMenuOpen(false)}>👤 Edit Profile</Link>
                {user.role === 'mentor' && <Link to="/dashboard/mentor/earnings" className="user-dd-item" onClick={() => setUserMenuOpen(false)}>💰 Earnings</Link>}
                {user.role === 'mentee' && <Link to="/dashboard/mentee/billing" className="user-dd-item" onClick={() => setUserMenuOpen(false)}>💳 Billing</Link>}
                {user.role === 'mentee' && <Link to="/become-mentor" className="user-dd-item" onClick={() => setUserMenuOpen(false)}>🎓 Become a Mentor</Link>}
                <div className="user-dd-divider" />
                {/* T&C Acceptance Record — always visible */}
                <div style={{ padding:'10px 14px', background:'#f0fdf4', margin:'0 0 2px', borderTop:'1px solid #f3f4f6' }}>
                  <div style={{ fontSize:'.6rem', fontWeight:800, textTransform:'uppercase', letterSpacing:'.06em', color:'#16a34a', marginBottom:4 }}>
                    ✅ Terms Accepted
                  </div>
                  {user.termsAccepted ? (
                    <>
                      <div style={{ fontSize:'.68rem', color:'#374151' }}>
                        Terms v{user.termsAccepted.version} · Privacy v{user.termsAccepted.privacyVersion}
                      </div>
                      <div style={{ fontSize:'.65rem', color:'#9ca3af', marginTop:2 }}>
                        {new Date(user.termsAccepted.acceptedAt).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
                        {' · '}{user.termsAccepted.method}
                      </div>
                      <div style={{ display:'flex', gap:8, marginTop:5 }}>
                        <a href="/terms" target="_blank" rel="noreferrer" style={{ fontSize:'.62rem', color:'#16a34a', fontWeight:700, textDecoration:'none' }}>Terms ↗</a>
                        <a href="/privacy" target="_blank" rel="noreferrer" style={{ fontSize:'.62rem', color:'#16a34a', fontWeight:700, textDecoration:'none' }}>Privacy ↗</a>
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize:'.68rem', color:'#9ca3af' }}>Accepted on account creation</div>
                  )}
                </div>
                <div className="user-dd-divider" />
                <button className="user-dd-item user-dd-logout" onClick={handleLogout}>🚪 Sign Out</button>
              </div>
            )}
          </div>
        ) : (
          <>
            <button className="btn btn-ghost" onClick={onSignIn}>Sign In</button>
            <button className="btn btn-primary" onClick={() => setModalOpen(true)}>Join Free →</button>
          </>
        )}
        <button className="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)}>☰</button>
      </div>

      {mobileOpen && (
        <div className="mobile-menu">
          {navLinks.map(link => (
            <Link key={link.path} to={link.path} className={`mobile-nav-tab ${location.pathname === link.path ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>{link.label}</Link>
          ))}
          {user && <Link to={DASHBOARD_ROUTES[user.role]} className="mobile-nav-tab" style={{ color: '#16a34a' }} onClick={() => setMobileOpen(false)}>📊 My Dashboard</Link>}
          <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginTop: 4 }}>
            <div style={{ fontSize: '.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--ink4)', marginBottom: 8 }}>Currency</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {CURRENCIES.map(c => (<button key={c.code} className={`curr-mobile-btn ${currency === c.code ? 'on' : ''}`} onClick={() => { setCurrency(c.code); setMobileOpen(false); }}>{c.flag} {c.code}</button>))}
            </div>
          </div>
          {user ? (
            <button className="btn btn-outline" style={{ marginTop: 8, width: '100%', justifyContent: 'center' }} onClick={() => { handleLogout(); setMobileOpen(false); }}>Sign Out</button>
          ) : (
            <button className="btn btn-primary" style={{ marginTop: 8, width: '100%', justifyContent: 'center' }} onClick={() => { setModalOpen(true); setMobileOpen(false); }}>Join Free →</button>
          )}
        </div>
      )}
    </nav>
  );
}
